import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { apiJson } from '../api/client.js';
import { CHAT_SOCKET_PATH, getChatSocketUrl } from '../utils/chatSocket.js';
import { useAuth } from './AuthContext.jsx';

const NotificationSocketContext = createContext(null);

async function fetchNotificationSocketToken() {
  return apiJson('/api/notifications/socket-token', { method: 'POST' });
}

export function NotificationSocketProvider({ children }) {
  const { user } = useAuth();
  const enabled = Boolean(user?.id);
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const notify = useCallback((notification) => {
    listenersRef.current.forEach((fn) => fn(notification));
    window.dispatchEvent(new CustomEvent('portal-notification', { detail: notification }));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const { token } = await fetchNotificationSocketToken();
        if (cancelled) return;

        const socket = io(getChatSocketUrl(), {
          path: CHAT_SOCKET_PATH,
          auth: { token },
          transports: ['polling', 'websocket'],
          reconnection: true,
          reconnectionAttempts: Infinity,
        });
        socketRef.current = socket;

        socket.on('notification:new', notify);
      } catch {
        // Polling fallback in NotificationsBell handles offline socket.
      }
    })();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [enabled, notify]);

  return (
    <NotificationSocketContext.Provider value={{ subscribe }}>
      {children}
    </NotificationSocketContext.Provider>
  );
}

export function useNotificationSocket() {
  const ctx = useContext(NotificationSocketContext);
  if (!ctx) {
    throw new Error('useNotificationSocket must be used within NotificationSocketProvider');
  }
  return ctx;
}
